import i18n from "i18next";
import { useState } from "react";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType";
import {
	TestConfigurationContextDataFormApi,
	TestConfigurationContextDataFormApi_InitData,
} from "src/api/generated/erp/configurator/api/configuratorTesting/testConfigurationContextDataFormApi.ts";
import { getCatalogPartRevisionStateLabel } from "src/api/generated/erp/db/types/enums/catalogPartRevisionState.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { AavoSelectField } from "src/components/common/inputFields/AavoSelectField.tsx";

interface RealObjectTestConfiguratorRootRevisionDialogProps {
	configuratorType: PartConfiguratorType;
	closeDialog: () => Promise<void>;
	onSubmit: () => Promise<void>;
}

export const RealObjectTestConfiguratorRootRevisionDialog = ({
	configuratorType,
	closeDialog,
	onSubmit,
}: RealObjectTestConfiguratorRootRevisionDialogProps) => (
	<AsyncFetchRender
		fetch={() =>
			TestConfigurationContextDataFormApi.getInitData({
				configuratorType: configuratorType,
			})
		}
		content={(initData) => (
			<Content
				configuratorType={configuratorType}
				closeDialog={closeDialog}
				onSubmit={onSubmit}
				initData={initData}
			/>
		)}
	/>
);

interface ContentProps extends RealObjectTestConfiguratorRootRevisionDialogProps {
	initData: TestConfigurationContextDataFormApi_InitData;
}

const Content = ({ configuratorType, closeDialog, onSubmit, initData }: ContentProps) => {
	const [rootCatalogPartRevisionIdToUse, setRootCatalogPartRevisionIdToUse] = useState<number | null>(
		initData.contextData.rootCatalogPartRevisionIdToUse ?? initData.activeRootRevisionId ?? null,
	);

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<VerticalBox sx={{ padding: 2, flex: 1 }}>
				<AavoSelectField
					label={i18n.t("root_revision_to_use")}
					value={rootCatalogPartRevisionIdToUse}
					disableClearable
					options={initData.rootRevisionOptions}
					getOptionKey={(rev) => rev.catalogPartRevisionId}
					getOptionLabel={(rev) => `${rev.revisionNumber} | ${getCatalogPartRevisionStateLabel(rev.state)}`}
					onChange={(revisionId) => setRootCatalogPartRevisionIdToUse(revisionId)}
				/>
			</VerticalBox>
			<AavoFormFooter>
				<AsyncButton label={i18n.t("cancel")} color={"secondary"} onClick={closeDialog} />
				<AsyncButton
					label={i18n.t("open")}
					disabled={rootCatalogPartRevisionIdToUse == null}
					onClick={async () => {
						await TestConfigurationContextDataFormApi.saveTestConfigurationContextData({
							configuratorType: configuratorType,
							contextData: {
								...initData.contextData,
								rootCatalogPartRevisionIdToUse: rootCatalogPartRevisionIdToUse,
							},
						});
						await closeDialog();
						await onSubmit();
					}}
				/>
			</AavoFormFooter>
		</VerticalBox>
	);
};
